import { PageWrapper, PageHeader, PopupTypes } from 'components/partials';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useAsync } from '../utils/customHooks';
import { useQuery } from '@apollo/client';
import { Translate, withLocalize } from 'react-localize-redux';
import { getCustomAIDimensions, deleteCustomAIDimension, updateCustomAIDimension } from './services';
import toaster from '../utils/toaster';
import CustomAIDimension from './components/CustomAIDimension';
import css from './page.css';
import { Spinner, Button } from '../components/widgets';
import SearchTextField from '../components/molecules/SearchTextField/SearchTextField';
import { IconSizes } from '../components/foundations/Icon';
import {
    CustomAIDimensionContext,
    MIXPANEL,
    TRANSLATION_PREFIX,
    buildMixpanelPayload,
    DIMENSIONS_LIMIT,
    EMPTY_DIMENSION,
    POPUP_DELAY,
    TOASTER_TRANSLATIONS,
} from './consts';
import { trackMixpanelEvent } from '../utils/general';
import AIDimensionsEmptyState from './components/AIDimensionsEmptyState';
import GeneralPopup from '../components/organisms/GeneralPopup';
import { useSelector } from 'react-redux';
import { getUserData } from '../mta/selectors';
import { ROLE_RESTRICTED } from '../teamManagement/utils';
import { GET_USER_APPS } from '../creativeGallery/queries';

const SUPPORT_LINK =
    'https://support.singular.net/hc/en-us/articles/33037500937243-Creative-Gallery-FAQ&utm_source=singular';

const NEW_DIMENSION_KEY = 'new-dimension';
const PAGE_HEADER_TEXT = `${TRANSLATION_PREFIX}.PAGE_HEADER`;
const SEARCH_PLACEHOLDER = `${TRANSLATION_PREFIX}.SEARCH_PLACEHOLDER`;
const ADD_NEW_AI_DIMENSION = `${TRANSLATION_PREFIX}.ADD_NEW_AI_DIMENSION`;

const ManageAIDimensions = ({ translate }) => {
    const [dimensionsResponse, runFetchDimensions] = useAsync({ isLoading: true });
    const [dimensions, setDimensions] = useState([]);
    const [expandedDimensionId, setExpandedDimensionId] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [isCreatingDimension, setIsCreatingDimension] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [dimensionToDelete, setDimensionToDelete] = useState(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showUnsavedChangesPopup, setShowUnsavedChangesPopup] = useState(false);

    const { loading: isAppsLoading, data: appsResponse } = useQuery(GET_USER_APPS);

    const apps = useMemo(
        () => (appsResponse?.userApps || []).map(app => ({ value: app.appId, label: app.displayName || app.name })),
        [appsResponse]
    );

    const userData = useSelector(state => getUserData(state));

    const isReadOnly = userData.role_type === ROLE_RESTRICTED;

    const filteredDimensions = useMemo(() => {
        const lowerSearchText = searchText.toLowerCase();
        return dimensions.filter(dimension => {
            return dimension.display_name.toLowerCase().includes(lowerSearchText);
        });
    }, [dimensions, searchText]);

    const handleSearch = text => {
        if (hasUnsavedChanges || (isCreatingDimension && expandedDimensionId === NEW_DIMENSION_KEY)) {
            setShowUnsavedChangesPopup(true);
            return;
        }

        setSearchText(text);
        setExpandedDimensionId(null);
    };

    const handleExpandDimension = dimensionId => {
        if (hasUnsavedChanges || (isCreatingDimension && expandedDimensionId === NEW_DIMENSION_KEY)) {
            setShowUnsavedChangesPopup(true);
            return;
        }

        setExpandedDimensionId(prevExpandedDimensionId =>
            prevExpandedDimensionId === dimensionId ? null : dimensionId
        );
    };

    const resetEditingState = () => {
        setExpandedDimensionId(null);
        setHasUnsavedChanges(false);
    };

    const handleRevertAndClose = () => {
        setShowUnsavedChangesPopup(false);
        resetEditingState();

        if (isCreatingDimension) setIsCreatingDimension(false);
    };

    const handleCloseCancellation = () => {
        setShowUnsavedChangesPopup(false);
    };

    const handleUpdateDimension = updatedDimension => {
        setDimensions(prev =>
            prev.map(dim => (dim.id === updatedDimension.id ? { ...dim, ...updatedDimension } : dim))
        );
        resetEditingState();
        onSaveApproved();
    };

    const handleDeleteDimension = dimension => {
        setDimensionToDelete(dimension);
    };

    const onSaveApproved = () => {
        setShowSaveDialog(true);
    };

    const onCloseSavePopup = () => {
        setShowSaveDialog(false);
    };

    const onDeleteApproved = async () => {
        try {
            await deleteCustomAIDimension(dimensionToDelete.id);
            setDimensions(prev => prev.filter(dim => dim.id !== dimensionToDelete.id));
            toaster.success(translate(TOASTER_TRANSLATIONS.DELETE_SUCCESS), { autoClose: POPUP_DELAY });
            resetEditingState();

            trackMixpanelEvent(
                MIXPANEL.EVENT_PREFIX,
                MIXPANEL.ACTIONS.DELETE,
                buildMixpanelPayload(dimensionToDelete, apps)
            );
        } catch {
            toaster.error(translate(TOASTER_TRANSLATIONS.DELETE_ERROR), { autoClose: POPUP_DELAY });
        } finally {
            setDimensionToDelete(null);
        }
    };

    const onDeleteRejected = () => {
        setDimensionToDelete(null);
    };

    const doesDisplayNameExist = useCallback(
        displayName => {
            const normalizedName = displayName.trim().toLowerCase();
            return dimensions.some(dimension => dimension.display_name.toLowerCase() === normalizedName);
        },
        [dimensions]
    );

    const handleAddNew = () => {
        if (hasUnsavedChanges) {
            setShowUnsavedChangesPopup(true);
            return;
        }
        setIsCreatingDimension(true);
        setExpandedDimensionId(NEW_DIMENSION_KEY);
    };

    const handleCancelCreate = () => {
        setIsCreatingDimension(false);
        resetEditingState();
    };

    const handleCreateDimension = async newDimension => {
        if (doesDisplayNameExist(newDimension.display_name)) {
            toaster.error(translate(TOASTER_TRANSLATIONS.DUPLICATE_NAME_ERROR), { autoClose: POPUP_DELAY });
            return;
        }

        try {
            const createdDimension = await updateCustomAIDimension(newDimension);
            setDimensions(prev => [createdDimension, ...prev]);
            setIsCreatingDimension(false);
            resetEditingState();
            onSaveApproved();
        } catch {
            toaster.error(translate(TOASTER_TRANSLATIONS.CREATE_ERROR), { autoClose: POPUP_DELAY });
        }
    };

    useEffect(() => {
        runFetchDimensions(getCustomAIDimensions());
    }, []);

    useEffect(() => {
        if (dimensionsResponse.data) {
            setDimensions(dimensionsResponse.data);
        }
        if (dimensionsResponse.error) {
            toaster.error(translate(TOASTER_TRANSLATIONS.FETCH_ERROR), { autoClose: POPUP_DELAY });
        }
    }, [dimensionsResponse.data, dimensionsResponse.error]);

    return (
        <CustomAIDimensionContext.Provider value={{ isReadOnly, apps, isAppsLoading }}>
            <PageWrapper className={css.wrapper}>
                <PageHeader
                    text={PAGE_HEADER_TEXT}
                    subText={{
                        faqLink: SUPPORT_LINK,
                    }}
                />
                <div className={css.container}>
                    {dimensionsResponse.isLoading ? (
                        <Spinner className={css.spinner} show expanded />
                    ) : !dimensions.length && !isCreatingDimension ? (
                        <AIDimensionsEmptyState isReadOnly={isReadOnly} containerClass={css.emptyState} onCreateDimension={handleAddNew} />
                    ) : (
                        <>
                            <div className={css.toolbar}>
                                <SearchTextField
                                    search={searchText}
                                    onSearchChange={handleSearch}
                                    placeholder={SEARCH_PLACEHOLDER}
                                    iconSize={IconSizes.MEDIUM}
                                    clearable
                                    className={css.searchField}
                                />
                                <div className={css.addContainer}>
                                    <div className={css.dimensionCount}>
                                        <span className={css.dimensionCountValue}>
                                            {dimensions.length} of {DIMENSIONS_LIMIT}
                                        </span>
                                        <span className={css.dimensionCountLabel}>
                                            <Translate id={`${TRANSLATION_PREFIX}.DIMENSIONS_LABEL`} />
                                        </span>
                                    </div>
                                    {!isReadOnly && (
                                        <Button
                                            type="primary"
                                            onClick={handleAddNew}
                                            disabled={isCreatingDimension || dimensions.length >= DIMENSIONS_LIMIT}
                                            Icon="plus"
                                            iconSize={IconSizes.X_SMALL}
                                            text={ADD_NEW_AI_DIMENSION}
                                        />
                                    )}
                                </div>
                            </div>
                            {isCreatingDimension && (
                                <CustomAIDimension
                                    key={NEW_DIMENSION_KEY}
                                    customAIDimension={EMPTY_DIMENSION}
                                    expanded={expandedDimensionId === NEW_DIMENSION_KEY}
                                    onExpand={() => handleExpandDimension(NEW_DIMENSION_KEY)}
                                    onUpdate={handleCreateDimension}
                                    onCancel={handleCancelCreate}
                                    onHasChangesChange={setHasUnsavedChanges}
                                />
                            )}
                            {filteredDimensions.map(dimension => (
                                <CustomAIDimension
                                    key={dimension.id}
                                    customAIDimension={dimension}
                                    expanded={expandedDimensionId === dimension.id}
                                    onExpand={() => handleExpandDimension(dimension.id)}
                                    onUpdate={handleUpdateDimension}
                                    onDelete={() => handleDeleteDimension(dimension)}
                                    onCancel={resetEditingState}
                                    onHasChangesChange={setHasUnsavedChanges}
                                />
                            ))}
                        </>
                    )}
                </div>
                <GeneralPopup
                    open={!!dimensionToDelete}
                    type={PopupTypes.DELETE}
                    title={`${TRANSLATION_PREFIX}.DELETE_DIMENSION_MODAL.TITLE`}
                    text={`${TRANSLATION_PREFIX}.DELETE_DIMENSION_MODAL.MESSAGE`}
                    acceptText={`${TRANSLATION_PREFIX}.DELETE_DIMENSION_MODAL.ACCEPT_BUTTON`}
                    rejectText={`${TRANSLATION_PREFIX}.DELETE_DIMENSION_MODAL.REJECT_BUTTON`}
                    onAccept={onDeleteApproved}
                    onReject={onDeleteRejected}
                    showSpinner={false}
                />
                <GeneralPopup
                    open={showSaveDialog}
                    type={PopupTypes.INFO}
                    title={`${TRANSLATION_PREFIX}.SAVE_DIMENSION_MODAL.TITLE`}
                    text={`${TRANSLATION_PREFIX}.SAVE_DIMENSION_MODAL.MESSAGE`}
                    acceptText={`${TRANSLATION_PREFIX}.SAVE_DIMENSION_MODAL.ACCEPT_BUTTON`}
                    onReject={onCloseSavePopup}
                    showSpinner={false}
                />
                <GeneralPopup
                    open={showUnsavedChangesPopup}
                    type={PopupTypes.WARNING_WITH_CANCEL}
                    title={`${TRANSLATION_PREFIX}.UNSAVED_CHANGES_MODAL.TITLE`}
                    text={`${TRANSLATION_PREFIX}.UNSAVED_CHANGES_MODAL.MESSAGE`}
                    acceptText={`${TRANSLATION_PREFIX}.UNSAVED_CHANGES_MODAL.REVERT_BUTTON`}
                    rejectText={`${TRANSLATION_PREFIX}.UNSAVED_CHANGES_MODAL.CANCEL_BUTTON`}
                    onAccept={handleRevertAndClose}
                    onReject={handleCloseCancellation}
                />
            </PageWrapper>
        </CustomAIDimensionContext.Provider>
    );
};

export default withLocalize(ManageAIDimensions);
